import { endpoints } from "../../../endpoints";
import { IUserInfo } from "../../core/Learn/api";
import {
  AddProgress,
  ContentGenerateBody,
  ICodeExecution,
} from "../../core/Module/api";
import { ProfilUpdateBody } from "../../core/Profile/api";
import { GenerateLessonBody, AlternateBody } from "../../internal/courses/api";
import { apiClient } from "./axios.interceptor";

export const API = {
  course: async (id: string) => {
    const { data } = await apiClient.get<ICourse>(endpoints.course.get(id));
    return data;
  },
  fetchCourses: async (body: any) => {
    const { data } = await apiClient.post<IApiListResponse<ICourse[]>>(
      endpoints.course.list,
      body
    );
    return data;
  },
  updateCourse: async (id: string, body: FormData | object) => {
    const isFormData = body instanceof FormData;

    const { data } = await apiClient.put<IApiResponse<ICourse>>(
      endpoints.course.put(id),
      body,
      {
        headers: {
          "Content-Type": isFormData
            ? "multipart/form-data"
            : "application/json",
        },
      }
    );

    return data;
  },
  createCourse: async (body: Partial<ICourse>) => {
    const { data } = await apiClient.post<ICourse>(
      endpoints.course.create,
      body
    );
    return data;
  },
  publishCourse: async (id: string) => {
    const { data } = await apiClient.post<ICourse>(
      endpoints.course.publish(id)
    );
    return data;
  },
  clearCourseChanges: async (id: string) => {
    const { data } = await apiClient.patch<ICourse>(
      endpoints.course.clearChanges(id)
    );
    return data;
  },
  deleteCourse: async (id: string) => {
    const { data } = await apiClient.delete(endpoints.course.delete(id));
    return data;
  },
  uploadMedia: async (body: FormData) => {
    const { data } = await apiClient.post<IApiResponse<string[]>>(
      endpoints.course.uploadMedia,
      body
    );
    return data;
  },
  removeMedia: async (body: any) => {
    const { data } = await apiClient.post<IApiResponse<string>>(
      endpoints.course.removeMedia,
      body
    );
    return data;
  },
  getModule: async (id: string) => {
    const { data } = await apiClient.get(endpoints.module.get(id));
    return data;
  },
  consumeModule: async (id: string) => {
    const { data } = await apiClient.get(endpoints.module.consume(id));
    return data;
  },
  createModule: async (body: Partial<IModule<any>>): Promise<IModule<any>> => {
    const { data } = await apiClient.post(endpoints.module.create, body);
    return data;
  },
  deleteModule: async (id: string): Promise<IApiResponse<IModule<any>>> => {
    const { data } = await apiClient.delete(endpoints.module.delete(id));
    return data;
  },
  saveLesson: async (body: ILesson): Promise<IApiResponse<any>> => {
    const { data } = await apiClient.post(endpoints.content.saveLesson, body);
    return data;
  },
  savePractice: async (body: IPractice): Promise<IApiResponse<any>> => {
    const { data } = await apiClient.post(endpoints.content.savePractice, body);
    return data;
  },
  updateModule: async (
    body: Partial<IModule<any>>
  ): Promise<IApiResponse<IModule<any>>> => {
    const { data } = await apiClient.put(
      endpoints.module.update(body._id!),
      body
    );
    return data;
  },
  createContent: async (
    body: Partial<ILesson>
  ): Promise<IApiResponse<ILesson>> => {
    const { data } = await apiClient.post(endpoints.content.create, body);
    return data;
  },
  deleteContent: async (id: string): Promise<IApiResponse<ILesson>> => {
    const { data } = await apiClient.delete(endpoints.content.delete(id));
    return data;
  },
  regenerateContent: async (body: ContentGenerateBody) => {
    const { data } = await apiClient.post(
      endpoints.content.regenerateContent,
      body
    );
    return data;
  },
  userInfo: async () => {
    const { data } = await apiClient.get<IApiResponse<IUserInfo>>(
      endpoints.users.info
    );
    return data;
  },
  fetchUsers: async (params: any) => {
    const { data } = await apiClient.get<IApiListResponse<IUser[]>>(
      endpoints.users.list,
      { params }
    );
    return data;
  },
  editUser: async (id: string, body: Partial<IUser>) => {
    const { data } = await apiClient.post(endpoints.users.edit(id), body);
    return data;
  },
  createUser: async (body: Partial<IUser>) => {
    const { data } = await apiClient.post(endpoints.users.create, body);
    return data;
  },
  coursesProgress: async () => {
    const { data } = await apiClient.get<
      IApiListResponse<CourseProgressResponse[]>
    >(endpoints.progress.courses);
    return data;
  },
  courseProgress: async (id: string) => {
    const { data } = await apiClient.get<
      IApiListResponse<CourseProgressResponse[]>
    >(endpoints.progress.course(id));
    return data;
  },
  addProgress: async (body: AddProgress) => {
    const { data } = await apiClient.post<IApiResponse<any>>(
      endpoints.progress.addProgress,
      body
    );
    return data;
  },
  profile: async () => {
    const { data } = await apiClient.get<IApiResponse<IUser>>(
      endpoints.users.profile
    );
    return data;
  },
  updateProfile: async (body: ProfilUpdateBody) => {
    const { data } = await apiClient.post<
      IApiResponse<{ user: IUser; message: string }>
    >(endpoints.users.updateProfile, body);
    return data;
  },
  resetModuleProgress: async (body: { moduleId: string }) => {
    const { data } = await apiClient.post<IApiResponse<any>>(
      endpoints.progress.reset,
      body
    );
    return data;
  },
  publicCourses: async () => {
    const { data } = await apiClient.get<IApiResponse<IUser>>(
      endpoints.public.courses
    );
    return data;
  },
  publicCourse: async (id: string) => {
    const { data } = await apiClient.get<IApiResponse<IUser>>(
      endpoints.public.course(id)
    );
    return data;
  },
  fetchSubscription: async () => {
    const { data } = await apiClient.get<IApiListResponse<ICourse[]>>(
      endpoints.payments.subscription
    );
    return data;
  },
  cancelSubsription: async () => {
    const { data } = await apiClient.post<IApiResponse<any>>(
      endpoints.payments.cancelSubscription
    );
    return data;
  },
  generateLesson: async (body: GenerateLessonBody) => {
    const { data } = await apiClient.post<IApiResponse<any>>(
      endpoints.content.generateLesson,
      body
    );
    return data;
  },
  alternateContent: async (body: AlternateBody) => {
    const { data } = await apiClient.post<IApiResponse<any>>(
      endpoints.content.alternateContent,
      body
    );
    return data;
  },
  publishLesson: async (body: { _id: string }) => {
    const { data } = await apiClient.post(
      endpoints.content.publishLesson,
      body
    );
    return data;
  },
  publishPractice: async (body: { _id: string }) => {
    const { data } = await apiClient.post(
      endpoints.content.publishPractice,
      body
    );
    return data;
  },
  deleteLesson: async (id: string): Promise<IApiResponse<string>> => {
    const { data } = await apiClient.delete(endpoints.content.deleteLesson(id));
    return data;
  },
  deletePractice: async (id: string): Promise<IApiResponse<string>> => {
    const { data } = await apiClient.delete(
      endpoints.content.deletePractice(id)
    );
    return data;
  },
  getTools: async () => {
    const { data } = await apiClient.get(endpoints.content.getTools);
    return data;
  },
  codeExecute: async (body: ICodeExecution) => {
    const { data } = await apiClient.post(endpoints.content.codeExecute, body);
    return data;
  },
  requestEarlyAccess: async (body: any) => {
    const { data } = await apiClient.post(
      endpoints.users.requestEarlyAccess,
      body
    );
    return data;
  },
  chatbot: async (body: any) => {
    const { data } = await apiClient.post(endpoints.content.chatbot, body);
    return data;
  },
};
